/**
 * Discipline icons and person avatars. Icons are a built-in set picked by name in each
 * discipline's config; colors come from the config, with starting looks guessed from the
 * discipline's key and label for projects that have not set them.
 */
import type { CSSProperties, ReactNode } from "react";
import type { ProjectConfig } from "@super-gantt/engine";
import { guessDisciplineStyle } from "@super-gantt/core";

/** 24×24 stroke icons. Keys are what a discipline's `icon` field names. */
export const DISCIPLINE_ICONS: Record<string, { label: string; body: ReactNode }> = {
  code: { label: "Code", body: <path d="M8 7l-5 5 5 5M16 7l5 5-5 5M13.5 4l-3 16" /> },
  dice: { label: "Dice", body: <><rect x="4" y="4" width="16" height="16" rx="3.5" /><circle cx="8.6" cy="8.6" r="1.2" fill="currentColor" /><circle cx="12" cy="12" r="1.2" fill="currentColor" /><circle cx="15.4" cy="15.4" r="1.2" fill="currentColor" /></> },
  pencil: { label: "Pencil", body: <><path d="M4 20l1.2-4.4L15.8 5a2 2 0 0 1 2.9 0l.3.3a2 2 0 0 1 0 2.9L8.4 18.8z" /><path d="M14 7l3 3" /></> },
  cube: { label: "Cube", body: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><path d="M4 7.5l8 4.5 8-4.5M12 12v9" /></> },
  ball: { label: "Bouncing ball", body: <><circle cx="17" cy="7" r="3.2" /><path d="M3 20h18" /><path d="M4 17c1.2-3.6 3.4-7 8.6-9.2" strokeDasharray="1.5 3" /></> },
  nodes: { label: "Nodes", body: <><rect x="3" y="4" width="7" height="6" rx="1.6" /><rect x="14" y="14" width="7" height="6" rx="1.6" /><path d="M10 7c5 0 0 10 4 10" /></> },
  wave: { label: "Waveform", body: <path d="M3 12h1.5M7 9v6M11 5v14M15 8v8M19 10.5v3" /> },
  speech: { label: "Speech", body: <><path d="M4 5h16v11h-9l-4.5 4v-4H4z" /><path d="M8 9h8M8 12.3h5" /></> },
  bug: { label: "Bug", body: <><rect x="7" y="8" width="10" height="12" rx="5" /><path d="M9.5 8.3a2.5 2.5 0 0 1 5 0M12 12v8M3.5 13H7M17 13h3.5M4.5 8.5L7.4 10M19.5 8.5L16.6 10M4.5 18.5L7.4 17M19.5 18.5L16.6 17" /></> },
  star: { label: "Star", body: <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z" /> },
};

const FALLBACK = ["#7FA6C9", "#C9A27F", "#9CC97F", "#C97FA6", "#7FC9C0", "#B9B06A"];

export interface DiscMeta { key: string; label: string; icon: string; color: string; outsourced: boolean }

export function discMeta(config: ProjectConfig, key: string): DiscMeta {
  const d = config.disciplines.find(x => x.key === key);
  if (!d) return { key, label: key, icon: "star", color: "#8A97A8", outsourced: false };
  const g = guessDisciplineStyle(d);
  const i = config.disciplines.indexOf(d);
  return {
    key, label: d.label,
    icon: d.icon && DISCIPLINE_ICONS[d.icon] ? d.icon : g?.icon || "star",
    color: d.color || g?.color || FALLBACK[i % FALLBACK.length],
    outsourced: d.outsourced ?? g?.outsourced ?? false,
  };
}

export function DiscIcon({ icon, size = 14, color, strokeWidth = 2.2, style }: { icon: string; size?: number; color?: string; strokeWidth?: number; style?: CSSProperties }) {
  const ic = DISCIPLINE_ICONS[icon] || DISCIPLINE_ICONS.star;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
      style={{ display: "block", flexShrink: 0, color, fill: "none", stroke: "currentColor", strokeWidth, strokeLinecap: "round", strokeLinejoin: "round", ...style }}>
      {ic.body}
    </svg>
  );
}

/** Two letters from a name: "Brad" → BR, "Concept 1" → C1, "JR (0.4)" → JR. */
export function initials(name: string) {
  const words = name.replace(/\(.*?\)/g, " ").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1 || /^\d/.test(words[0])) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** A person's circle: initials on their primary discipline's color, with that discipline's icon as a corner badge. */
export function Avatar({ name, disc, size = 32, badge = true, selected = false, title, onClick }: {
  name: string; disc: DiscMeta; size?: number; badge?: boolean; selected?: boolean; title?: string; onClick?: () => void;
}) {
  const bs = Math.max(13, Math.round(size * 0.44));
  const os = disc.outsourced;
  return (
    <div title={title ?? `${name} · ${disc.label}`} onClick={onClick} role={onClick ? "button" : undefined}
      style={{
        position: "relative", width: size, height: size, borderRadius: "50%", flexShrink: 0,
        display: "grid", placeItems: "center", cursor: onClick ? "pointer" : undefined,
        background: os ? "#232B36" : disc.color, color: os ? disc.color : "#14181E",
        boxShadow: `${os ? `inset 0 0 0 1.5px ${disc.color}, ` : ""}${selected ? "0 0 0 2px #151A21, 0 0 0 4px #E8A33D" : "none"}`,
        outline: os ? "1.5px dashed #5F6B7C" : "none", outlineOffset: 2,
        font: `700 ${Math.round(size * 0.46)}px/1 'Barlow', system-ui, sans-serif`, letterSpacing: "-0.02em",
        userSelect: "none",
      }}>
      {initials(name)}
      {badge && (
        <span style={{
          position: "absolute", right: -Math.round(bs * 0.36), bottom: -Math.round(bs * 0.36), width: bs, height: bs, borderRadius: "50%",
          background: "#151A21", display: "grid", placeItems: "center", boxShadow: `0 0 0 1.5px ${disc.color}`,
        }}>
          <DiscIcon icon={disc.icon} size={Math.round(bs * 0.68)} color={disc.color} strokeWidth={2.6} />
        </span>
      )}
    </div>
  );
}
